import sslImg from '../../assets/ssl.png'
import { lazy, useContext, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import { useTranslation } from '../../useTranslation'
import { useUser } from '../../common/hooks/auth/useUser'
import { useQueryWithLoader } from '../loader/useQueryWithLoader'
import { useShoppingCartApi } from '../../common/api-context'
import React from 'react'
import { GlobalContext } from '../../common/global-context'
import Button from '../common/button'
import Dropdown from '../common/dropdown'
import useKeyPress from '../../common/hooks/useKeyPress'
import { LanguageKey } from '@ringo/shared'
import { Dev } from './dev'

interface HeaderProps {
    responsive?: boolean
}

const Header: React.FC<HeaderProps> = ({ responsive = false }) => {
    const [showSlideOver, setShowSlideOver] = useState(false)
    const { t, supportedLanguages } = useTranslation()
    const { i18n } = useTranslation()
    const { updateUserLanguage, isVerified, user, removeUser } = useUser()
    const shoppingCartApi = useShoppingCartApi()
    const [devMode, setDevMode] = useState(false)
    const { settings } = useContext(GlobalContext)
    const [shouldMountSlideover, setShouldMountSlideover] = useState(false)
    const { data: shoppingCart } = useQueryWithLoader(
        'getShoppingCartItems',
        ['getShoppingCartItems', isVerified],
        async () => (await shoppingCartApi.getShoppingCart()).data,
        {
            staleTime: Number.POSITIVE_INFINITY,
            enabled: isVerified,
        }
    )

    useKeyPress({ metaKey: true, shiftKey: true, key: 'c' }, () => {
        setDevMode(true)
    })

    const ShoppingCartPreview = lazy(() => import('../../pages/shopping-cart/shopping-cart-preview'))

    useEffect(() => {
        setShouldMountSlideover(true)
    }, [showSlideOver])

    const uiSupportedLanguages = useMemo(() => {
        return supportedLanguages.filter((x) => settings.activeLanguages?.includes(x.id as any)).map((x) => ({ ...x, id: x.id.toLowerCase() }))
    }, [supportedLanguages, settings])

    return (
        <>
            {isVerified && shoppingCart && shouldMountSlideover && (
                <React.Suspense>
                    <ShoppingCartPreview showSlideOver={showSlideOver} setShowSlideOver={setShowSlideOver} onClose={() => setShowSlideOver(false)} />
                </React.Suspense>
            )}
            {devMode && <Dev isOpen={devMode} setIsOpen={setDevMode} />}
            <div className="bg-custom_gray h-11 relative">
                <header className="container px-4 flex max-tablet:max-w-full max-tablet:w-full max-tablet:px-7">
                    <div className="w-1/5">
                        <img className="mt-2 w-11" src={sslImg} alt="ssl" />
                    </div>
                    <div className="text-white w-80 flex-1 flex justify-end gap-4 flex-row items-center">
                        <div className="flex items-center gap-4 -mt-2.5">
                            {user?.impersonated && (
                                <div className="absolute mt-2.5 items-center flex gap-2 top-1/2 z-50 left-[300px] transform -translate-y-1/2 shadow-lg bg-white p-4 rounded-md">
                                    <span className="font-bold text-red-400">{t('impersonationOn')}</span>
                                    <Button text={t('leaveImpersonation')} type={'squareYellow'} onClick={removeUser}></Button>
                                </div>
                            )}
                            <span className="text-xs leading-none text-white  mt-[3px]">{user?.masterData?.companyName}</span>
                            {!settings?.maintenanceMode && (
                                <Link to="/account/overview" className="text-xs leading-none text-white cursor-pointer items-center">
                                    <FontAwesomeIcon icon={faUser} className="pr-2" />
                                    <span>{t('header.myAccount')}</span>
                                </Link>
                            )}
                            {isVerified && !settings?.maintenanceMode && (
                                <Link to="/favourites" className="text-xs leading-none text-white cursor-pointer ">
                                    <FontAwesomeIcon icon={['fas', 'file-arrow-down']} className="pr-2" />
                                    <span>{t('favoriteProfiles')}</span>
                                </Link>
                            )}
                        </div>
                        {!settings?.maintenanceMode && (
                            <Link to="/shopping-cart" data-cy="header-cart-link">
                                <div
                                    className={`bg-custom_yellow cursor-pointer relative p-2.5 flex items-center justify-center gap-5 ${
                                        responsive && 'max-xs:bg-transparent max-xs:p-0 max-xs:mt-0 max-xs:gap-0'
                                    }`}
                                    // onClick={() => setShowSlideOver(isVerified ? true : false)}
                                >
                                    <div>
                                        <FontAwesomeIcon
                                            icon={['fas', 'bag-shopping']}
                                            className={`px-2 text-2xl ${responsive && 'max-xs:px-0 max-xs:text-xl'}`}
                                        />
                                    </div>
                                    <div className="uppercase text-right  font-bold text-white leading-none">
                                        <p className={`text-[11px] leading-none uppercase font-bold ${responsive && 'max-xs:hidden'}`}>
                                            {t('homePage.yourBasket')}
                                        </p>
                                        <p
                                            className={`mt-1 text-sm leading-none ${
                                                responsive &&
                                                'max-xs:bg-[#333] max-xs:p-[5px] max-xs:rounded-[50%] max-xs:w-5 max-xs:h-5 max-xs:absolute max-xs:top-[3px] max-xs:right-[-10px] max-xs:mt-0 max-xs:text-center'
                                            }`}
                                        >
                                            <span className="cart-total-item" data-cy="header-cart-total">
                                                {shoppingCart?.totalRingQuantity}
                                            </span>
                                            <span className={`pl-[5px] ${responsive && 'max-xs:hidden'}`}>{t('products')}</span>
                                        </p>
                                    </div>
                                </div>
                            </Link>
                        )}

                        <Dropdown
                            options={uiSupportedLanguages}
                            value={i18n.language}
                            searchable={false}
                            className="border-none! language"
                            ulClassName="w-[200px]! right-0"
                            showDownIcon={false}
                            dataCy="language-dropdown"
                            renderSelectedOption={(option) => (
                                <div className="flex text-black gap-4">
                                    <img className="h-5" src={new URL(`../../assets/flags/${option?.id}.svg`, import.meta.url).href} alt={option?.label} />
                                </div>
                            )}
                            onChange={(val) => updateUserLanguage(val as LanguageKey)}
                            renderOption={(option) => (
                                <div className={`flex text-black gap-4 language-${option.id}`}>
                                    <img className="h-5" src={new URL(`../../assets/flags/${option.id}.svg`, import.meta.url).href} alt={option.label} />
                                    {option.label}
                                </div>
                            )}
                        />
                    </div>
                </header>
            </div>
        </>
    )
}

export default Header
